import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Search, X, Loader2 } from 'lucide-react';
import { searchApi } from '@/api/search';

interface Props {
  channelId: string;
  onClose: () => void;
  onJump?: (messageId: string) => void;
}

function useDebounced<T>(value: T, ms: number): T {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return v;
}

export default function ChannelSearchModal({ channelId, onClose, onJump }: Props) {
  const [q, setQ] = useState('');
  const debounced = useDebounced(q, 300);
  const enabled = debounced.trim().length >= 2;

  const { data, isLoading } = useQuery({
    queryKey: ['search', 'channel', channelId, debounced],
    queryFn: () => searchApi.inChannel(channelId, debounced, 50),
    enabled,
  });

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-start justify-center p-4 pt-[10vh]" onClick={onClose}>
      <div className="bg-surface-900 border border-surface-800 rounded-xl w-full max-w-lg shadow-2xl max-h-[80vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center gap-2 p-3 border-b border-surface-800">
          <Search size={16} className="text-surface-500" />
          <input
            autoFocus
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Поиск в этом канале…"
            className="flex-1 bg-transparent outline-none text-surface-100 text-sm"
          />
          {isLoading && enabled && <Loader2 size={14} className="animate-spin text-surface-500" />}
          <button onClick={onClose} className="text-surface-500 hover:text-surface-200"><X size={16} /></button>
        </div>
        <div className="flex-1 overflow-y-auto p-3">
          {!enabled && <p className="text-xs text-surface-500 text-center py-6">Введите минимум 2 символа</p>}
          {enabled && data && data.length === 0 && !isLoading && (
            <p className="text-xs text-surface-500 text-center py-6">Ничего не найдено</p>
          )}
          {data?.map((m) => (
            <button
              key={m.id}
              onClick={() => { onJump?.(m.id); onClose(); }}
              className="block w-full text-left px-2 py-1.5 rounded hover:bg-surface-800"
            >
              <p className="text-sm text-surface-200 truncate">{m.content}</p>
              <p className="text-[10px] text-surface-600">{new Date(m.createdAt).toLocaleString('ru-RU')}</p>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
